import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import "./OrderPages.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import AutoRefreshBar from "./AutoRefreshBar";
import { useSyncedOrderPagesAutoRefresh } from "./orderPagesAutoRefresh";
import usePageTitle from "./usePageTitle";

const METRICS_POLL_MS = 4000;
const FRESH_MS = 5 * 60 * 1000;
const DEFAULT_PANEL_ID = 4;

const loadedPanelsStorageKey = (orderId) => `orderChartLoadedPanels:v3:${orderId}`;

function readLoadedPanels(orderId) {
  if (!orderId) {
    return [DEFAULT_PANEL_ID];
  }
  try {
    const raw = sessionStorage.getItem(loadedPanelsStorageKey(orderId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const ids = parsed.filter((n) => Number.isInteger(n));
        if (ids.length) {
          return ids.includes(DEFAULT_PANEL_ID) ? ids : [DEFAULT_PANEL_ID, ...ids];
        }
      }
    }
  } catch {
    // ignore unreadable storage
  }
  return [DEFAULT_PANEL_ID];
}

function writeLoadedPanels(orderId, ids) {
  if (!orderId) {
    return;
  }
  try {
    sessionStorage.setItem(loadedPanelsStorageKey(orderId), JSON.stringify(ids));
  } catch {
    // ignore unwritable storage
  }
}

function isCommissionFresh(payload) {
  const s = payload?.compute_requested_at;
  if (!s) {
    return false;
  }
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) {
    return false;
  }
  return Date.now() - t < FRESH_MS;
}

function shouldRetryStaleComputingOrFailed(payload) {
  if (!payload) {
    return false;
  }
  if (payload.status === "computing") {
    return !isCommissionFresh(payload);
  }
  if (payload.status === "failed") {
    return !isCommissionFresh(payload);
  }
  return false;
}

function OrderPanelChart({ fetchWithAuth, orderId, panel, refreshNonce, onPanelMeta }) {
  const [figure, setFigure] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await fetchWithAuth(
          `/chart/${encodeURIComponent(orderId)}/panel/${panel.id}/`
        );
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const payload = await response.json();
        if (cancelled || !mountedRef.current) {
          return;
        }
        setFigure(payload?.figure ?? null);
        setError("");
        if (onPanelMeta) {
          onPanelMeta({
            line_no: payload?.line_no ?? null,
            skus: Array.isArray(payload?.skus) ? payload.skus : [],
            single_pack_offset_g: payload?.single_pack_offset_g ?? null,
          });
        }
      } catch (err) {
        if (!cancelled && mountedRef.current) {
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchWithAuth, orderId, panel.id, refreshNonce, onPanelMeta]);

  return (
    <div className="order-panel">
      <h3 className="order-panel-title">{panel.title}</h3>
      {error ? <div className="order-error">Error: {error}</div> : null}
      {loading && !figure ? (
        <div className="order-loader">
          <div className="order-spinner" />
          <p>Loading {panel.title}...</p>
        </div>
      ) : figure ? (
        <div className="order-chart-wrapper">
          <Plot
            data={figure.data || []}
            layout={figure.layout || {}}
            config={figure.config || { responsive: true }}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <div>No chart data available.</div>
      )}
    </div>
  );
}

function OrderChartPage({ orderId }) {
  usePageTitle(orderId ? `Order ${orderId}` : "Order");
  const [lineNo, setLineNo] = useState(null);
  const [skus, setSkus] = useState([]);
  const [singlePackOffsetG, setSinglePackOffsetG] = useState(null);
  const [panels, setPanels] = useState([]);
  const [panelsError, setPanelsError] = useState("");
  const [loadedPanels, setLoadedPanels] = useState(() => readLoadedPanels(orderId));
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState("");
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsError, setSnapshotsError] = useState("");
  const [openSnapshots, setOpenSnapshots] = useState({});
  const [autoRefresh, setAutoRefresh] = useSyncedOrderPagesAutoRefresh();
  const isMountedRef = useRef(true);
  const absentAutoAttemptedRef = useRef(false);
  const computePostInFlightRef = useRef(false);

  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);

  const fetchIntervalMs = useMemo(() => {
    const envInterval = process.env.REACT_APP_FETCH_INTERVAL_MS;
    const parsed = Number(envInterval);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60000;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    absentAutoAttemptedRef.current = false;
    setLineNo(null);
    setSkus([]);
    setSinglePackOffsetG(null);
    setLoadedPanels(readLoadedPanels(orderId));
  }, [orderId]);

  useEffect(() => {
    writeLoadedPanels(orderId, loadedPanels);
  }, [orderId, loadedPanels]);

  const handlePanelMeta = useCallback((meta) => {
    if (meta?.line_no != null) {
      setLineNo(meta.line_no);
    }
    if (Array.isArray(meta?.skus)) {
      setSkus(meta.skus);
    }
    if (meta?.single_pack_offset_g != null) {
      setSinglePackOffsetG(meta.single_pack_offset_g);
    }
  }, []);

  const loadPanel = useCallback((panelId) => {
    setLoadedPanels((prev) => (prev.includes(panelId) ? prev : [...prev, panelId]));
  }, []);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await auth.fetchWithAuth(`/chart/panels/`);
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const payload = await response.json();
        if (cancelled) {
          return;
        }
        setPanels(Array.isArray(payload?.panels) ? payload.panels : []);
        setPanelsError("");
      } catch (err) {
        if (!cancelled) {
          setPanelsError(err instanceof Error ? err.message : "Unknown error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.authChecked, auth.isAuthenticated, auth.fetchWithAuth]);

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }
    const id = setInterval(() => setRefreshNonce((n) => n + 1), fetchIntervalMs);
    return () => clearInterval(id);
  }, [autoRefresh, fetchIntervalMs]);

  const fetchMetricsPayload = useCallback(async () => {
    const response = await auth.fetchWithAuth(
      `/chart/${encodeURIComponent(orderId)}/metrics/`
    );
    const raw = await response.text();
    let payload = {};
    try {
      payload = raw ? JSON.parse(raw) : {};
    } catch {
      payload = {};
    }
    if (!response.ok) {
      const detail =
        typeof payload?.detail === "string"
          ? payload.detail
          : raw || `Request failed: ${response.status}`;
      throw new Error(detail);
    }
    return payload;
  }, [auth.fetchWithAuth, orderId]);

  const postCompute = useCallback(
    async (force) => {
      const params = force ? { force: "true" } : {};
      const response = await auth.fetchWithAuth(
        `/chart/${encodeURIComponent(orderId)}/metrics/compute/`,
        { method: "POST", params }
      );
      const raw = await response.text();
      let body = {};
      try {
        body = raw ? JSON.parse(raw) : {};
      } catch {
        body = {};
      }
      if (response.status === 503) {
        throw new Error(body?.detail || "Database unavailable");
      }
      if (!response.ok && response.status !== 202) {
        throw new Error(
          typeof body?.detail === "string"
            ? body.detail
            : raw || `Request failed: ${response.status}`
        );
      }
    },
    [auth.fetchWithAuth, orderId]
  );

  const maybeTriggerComputeFromPayload = useCallback(
    async (payload) => {
      if (!payload || payload.reason === "no_db") {
        return;
      }
      if (computePostInFlightRef.current) {
        return;
      }
      let should = false;
      if (payload.status === "absent" && !absentAutoAttemptedRef.current) {
        absentAutoAttemptedRef.current = true;
        should = true;
      } else if (shouldRetryStaleComputingOrFailed(payload)) {
        should = true;
      }
      if (!should) {
        return;
      }
      computePostInFlightRef.current = true;
      try {
        await postCompute(false);
        const p2 = await fetchMetricsPayload();
        if (isMountedRef.current) {
          setMetrics(p2);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setMetricsError(
            err instanceof Error ? err.message : "Unknown metrics error"
          );
        }
      } finally {
        computePostInFlightRef.current = false;
      }
    },
    [fetchMetricsPayload, postCompute]
  );

  const fetchMetrics = useCallback(async () => {
    if (!orderId || !auth.authChecked || !auth.isAuthenticated) {
      return;
    }
    try {
      const payload = await fetchMetricsPayload();
      if (!isMountedRef.current) {
        return;
      }
      setMetrics(payload);
      setMetricsError("");
      void maybeTriggerComputeFromPayload(payload);
    } catch (err) {
      if (isMountedRef.current) {
        setMetrics(null);
        setMetricsError(
          err instanceof Error ? err.message : "Unknown metrics error"
        );
      }
    }
  }, [
    orderId,
    auth.authChecked,
    auth.isAuthenticated,
    fetchMetricsPayload,
    maybeTriggerComputeFromPayload,
  ]);

  const manualEnqueue = useCallback(
    async (force) => {
      if (!orderId) {
        return;
      }
      setMetricsLoading(true);
      setMetricsError("");
      try {
        await postCompute(force);
        const p = await fetchMetricsPayload();
        if (isMountedRef.current) {
          setMetrics(p);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setMetricsError(
            err instanceof Error ? err.message : "Unknown metrics error"
          );
        }
      } finally {
        if (isMountedRef.current) {
          setMetricsLoading(false);
        }
      }
    },
    [fetchMetricsPayload, orderId, postCompute]
  );

  useEffect(() => {
    if (!orderId || !auth.authChecked || !auth.isAuthenticated) {
      return undefined;
    }
    fetchMetrics();
    const id = setInterval(fetchMetrics, METRICS_POLL_MS);
    return () => clearInterval(id);
  }, [orderId, auth.authChecked, auth.isAuthenticated, fetchMetrics]);

  const fetchSnapshots = useCallback(async () => {
    if (!orderId || !auth.authChecked || !auth.isAuthenticated) {
      return;
    }
    try {
      const response = await auth.fetchWithAuth(
        `/api/config/snapshots/order/${encodeURIComponent(orderId)}`,
        { params: { limit: 200 } }
      );
      if (!response.ok) {
        throw new Error(`Snapshots request failed: ${response.status}`);
      }
      const payload = await response.json();
      if (isMountedRef.current) {
        setSnapshots(Array.isArray(payload?.snapshots) ? payload.snapshots : []);
        setSnapshotsError("");
      }
    } catch (err) {
      if (isMountedRef.current) {
        setSnapshots([]);
        setSnapshotsError(err instanceof Error ? err.message : "Unknown snapshots error");
      }
    }
  }, [orderId, auth.authChecked, auth.isAuthenticated, auth.fetchWithAuth]);

  useEffect(() => {
    if (!orderId || !auth.authChecked || !auth.isAuthenticated) {
      return undefined;
    }
    fetchSnapshots();
    const id = setInterval(fetchSnapshots, METRICS_POLL_MS);
    return () => clearInterval(id);
  }, [orderId, auth.authChecked, auth.isAuthenticated, fetchSnapshots]);

  const fmtNum = (v, digits = 4) =>
    v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(digits);

  const metricsReady = metrics && metrics.status === "ready";
  const metricsComputing = metrics && metrics.status === "computing";
  const metricsFailed = metrics && metrics.status === "failed";
  const metricsAbsent = !metrics || metrics.status === "absent";
  const freshFailedOrComputing =
    (metricsComputing || metricsFailed) && isCommissionFresh(metrics);

  const formatSnapshotTime = (value) => {
    if (!value) {
      return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  };

  const toggleSnapshot = (snapshotId) => {
    setOpenSnapshots((prev) => ({
      ...prev,
      [snapshotId]: !prev[snapshotId],
    }));
  };

  return (
    <AuthGate auth={auth}>
      <div className="order-page">
        <div className="order-container">
          <a className="order-back-link" href="/order_list">
            Back to order list
          </a>
          <h1 className="order-title">
            Order Analysis: {orderId}
            {lineNo != null ? <span className="order-line-badge">Line: {lineNo}</span> : null}
            {skus.length > 0 ? (
              <span className="order-sku-badge" title={skus.join(", ")}>
                SKU: {skus.join(", ")}
              </span>
            ) : null}
            {singlePackOffsetG != null && singlePackOffsetG > 0 ? (
              <span
                className="order-ga-offset-badge"
                title="Pesymistyczna korekta GA odejmowana od każdej zaakceptowanej paczki na wykresach i w algorytmie"
              >
                GA offset: −{Number(singlePackOffsetG).toFixed(2)} g/pack
              </span>
            ) : null}
          </h1>
          <AutoRefreshBar
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            fetchIntervalMs={fetchIntervalMs}
            onRefreshNow={() => setRefreshNonce((n) => n + 1)}
            subtitleWhenOn={`Załadowane wykresy odświeżają się co ${Math.round(fetchIntervalMs / 1000)} s`}
            subtitleWhenOff="Odświeżanie co interwał jest wyłączone"
          />
          <div className="order-metrics-panel">
            <div className="order-metrics-actions">
              <button
                type="button"
                className="order-metrics-button"
                onClick={() => manualEnqueue(false)}
                disabled={
                  metricsLoading ||
                  !orderId ||
                  (metricsComputing && !metricsFailed)
                }
              >
                {metricsLoading
                  ? "Wysyłanie…"
                  : metricsComputing && !metricsFailed
                    ? "Liczenie w toku…"
                    : "Uruchom obliczenia (jeśli trzeba)"}
              </button>
              {metricsReady ? (
                <button
                  type="button"
                  className="order-metrics-button"
                  style={{ marginLeft: "0.5rem" }}
                  onClick={() => manualEnqueue(true)}
                  disabled={metricsLoading}
                >
                  Przelicz (nadpisz)
                </button>
              ) : null}
              {metricsFailed && freshFailedOrComputing ? (
                <button
                  type="button"
                  className="order-metrics-button"
                  style={{ marginLeft: "0.5rem" }}
                  onClick={() => manualEnqueue(true)}
                  disabled={metricsLoading}
                >
                  Wymuś ponowienie
                </button>
              ) : null}
            </div>
            <p className="order-metrics-hint">
              Metryki: odczyt z bazy co {METRICS_POLL_MS / 1000} s. Brak wiersza — jedna automatyczna
              próba zlecenia; zlecenie w ostatnich 5 min jest respektowane (serwer i UI); starsze
              „computing” / „failed” — ponowne zlecenie. Liczenie w osobnym wątku (asyncio).
            </p>
            {metricsAbsent && !metricsError ? (
              <p className="order-metrics-hint">
                Brak zapisanego wyniku — po zapisie tabela się wypełni.
              </p>
            ) : null}
            {metricsComputing ? (
              <p className="order-metrics-hint">
                Odpowiedź POST jest krótka; wynik pojawi się po zakończeniu joba.
              </p>
            ) : null}
            {metricsFailed ? (
              <div className="order-error order-metrics-error">
                Ostatnie obliczenie nie powiodło się: {metrics.error || "—"}
              </div>
            ) : null}
            {metricsError ? (
              <div className="order-error order-metrics-error">{metricsError}</div>
            ) : null}
            {metricsReady ? (
              <table className="order-metrics-table">
                <tbody>
                  <tr>
                    <th scope="row">Średnia wariancji wag (g²)</th>
                    <td>
                      {fmtNum(metrics.variance_mean_of_group_variances_g2, 4)}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Grupy (Vemag run, n≥2 paczki)</th>
                    <td>{metrics.variance_n_groups_ge2 ?? "—"}</td>
                  </tr>
                  <tr>
                    <th scope="row">Zaakceptowane dopasowane do Vemag</th>
                    <td>{metrics.variance_n_accepted_matched ?? "—"}</td>
                  </tr>
                  <tr>
                    <th scope="row">MSE current_mu vs observed (g²)</th>
                    <td>
                      {fmtNum(metrics.current_mu_vs_observed_shifted_mean_mse_g2, 6)}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Punkty MSE (ticki z oknem przesuniętym)</th>
                    <td>{metrics.current_mu_vs_observed_shifted_mean_n ?? "—"}</td>
                  </tr>
                  <tr>
                    <th scope="row">Delay / interval (s)</th>
                    <td>
                      {fmtNum(metrics.delay_seconds, 1)} /{" "}
                      {fmtNum(metrics.suggestion_interval_seconds, 1)}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : null}
          </div>
          {panelsError ? <div className="order-error">Error: {panelsError}</div> : null}
          <div className="order-panels">
            {panels.map((panel) =>
              loadedPanels.includes(panel.id) ? (
                <OrderPanelChart
                  key={panel.id}
                  fetchWithAuth={auth.fetchWithAuth}
                  orderId={orderId}
                  panel={panel}
                  refreshNonce={refreshNonce}
                  onPanelMeta={handlePanelMeta}
                />
              ) : (
                <div className="order-panel-placeholder" key={panel.id}>
                  <h3 className="order-panel-title">{panel.title}</h3>
                  <button
                    type="button"
                    className="order-metrics-button"
                    onClick={() => loadPanel(panel.id)}
                  >
                    Załaduj wykres
                  </button>
                </div>
              )
            )}
          </div>
          <div className="order-snapshots">
            <h2>Config snapshots used during this order</h2>
            {snapshotsError ? (
              <div className="order-error">{snapshotsError}</div>
            ) : null}
            {snapshots.length === 0 ? (
              <div className="order-snapshots-empty">No snapshots found yet.</div>
            ) : (
              <div className="order-snapshots-list">
                {snapshots.map((snapshot) => (
                  <div className="order-snapshot-card" key={snapshot.id ?? snapshot.created_at}>
                    <div className="order-snapshot-header">
                      <div>
                        <div className="order-snapshot-title">
                          {formatSnapshotTime(snapshot.created_at)}
                        </div>
                        <div className="order-snapshot-meta">
                          Scope: {snapshot.scope || "—"} · Line: {snapshot.line_no ?? "—"} · Key: {snapshot.changed_key || "—"}
                        </div>
                        <div className="order-snapshot-meta">
                          Source: {snapshot.source || "—"}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="order-snapshot-toggle"
                        onClick={() => toggleSnapshot(snapshot.id ?? snapshot.created_at)}
                      >
                        {openSnapshots[snapshot.id ?? snapshot.created_at] ? "Hide details" : "Show details"}
                      </button>
                    </div>
                    {openSnapshots[snapshot.id ?? snapshot.created_at] ? (
                      <pre className="order-snapshot-details">
                        {JSON.stringify(snapshot.payload ?? {}, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthGate>
  );
}

export default OrderChartPage;
